import React, { useState } from 'react';
import { ArrowRight, Search, FileCheck, Bell, Shield, Headphones, Sparkles, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import CounterAnimation from './CounterAnimation';
import RegistrationModal from './RegistrationModal';

const Hero = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const services = [
    { icon: Search, text: 'Trademark Search' },
    { icon: FileCheck, text: 'Registration Filing' },
    { icon: Shield, text: 'Brand Protection' },
    { icon: Bell, text: 'Monitoring Services' },
    { icon: Headphones, text: 'Expert Legal Support' }
  ];

  return (
    <div className="relative">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-[#2F1A8E] to-[#FF035B] text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-xs sm:text-sm text-center">
          <span className="hidden sm:inline">🎉 <span className="font-semibold">Limited Time:</span> Professional trademark registration starting at just $45 + filing fees - Protect your brand today!</span>
          <span className="sm:hidden">🎉 Trademark registration from <span className="font-semibold">$45</span> + filing fees!</span>
          <button 
            onClick={() => openModal('registration')}
            className="text-white hover:underline flex items-center gap-1 transition-colors font-semibold whitespace-nowrap flex-shrink-0"
          >
            Get Started <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>

      {/* Hero Content - Modern Gradient Style */}
      <div className="relative min-h-[700px] overflow-hidden bg-gradient-to-br from-[#2F1A8E] via-[#1a1a3e] to-black">
        {/* Animated Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#FF035B]/30 via-transparent to-[#FF035B]/20 animate-pulse"></div>
        <div className="absolute inset-0 bg-gradient-to-bl from-transparent via-[#2F1A8E]/20 to-transparent"></div>
        
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1718220216044-006f43e3a9b1" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-[#FF035B]/20 rounded-full blur-xl animate-bounce" style={{ animationDuration: '3s' }}></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-[#2F1A8E]/30 rounded-full blur-2xl animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
        <div className="absolute bottom-40 left-1/4 w-24 h-24 bg-[#FF035B]/20 rounded-full blur-xl animate-bounce" style={{ animationDuration: '5s', animationDelay: '2s' }}></div>
        <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-white/5 rounded-full blur-lg animate-bounce" style={{ animationDuration: '4s' }}></div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `linear-gradient(#FF035B 1px, transparent 1px), linear-gradient(90deg, #FF035B 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-[#FF035B]/40 px-4 py-2 rounded-full mb-8">
              <Sparkles className="w-4 h-4 text-[#FF035B] animate-pulse" />
              <span className="text-sm font-medium text-white">
                Trusted by <CounterAnimation end={10000} suffix="+" /> Growing Businesses
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Protect Your Brand Identity
              <br />
              <span className="bg-gradient-to-r from-[#FF035B] via-[#FF1493] to-[#FF69B4] bg-clip-text text-transparent" style={{ textShadow: '0 0 30px rgba(255, 3, 91, 0.3)' }}>
                with Expert Trademark Services
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-base md:text-lg text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Fast-track your trademark registration and secure your competitive edge. Get comprehensive brand protection with transparent pricing from just $45 + filing fees.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button 
                onClick={() => openModal('registration')}
                className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white font-bold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                Secure Your Trademark Now
              </button>
              <button 
                onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                className="border-2 border-white text-white hover:bg-white hover:text-[#2F1A8E] font-semibold px-8 py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Speak with an Expert
              </button>
            </div>

            <RegistrationModal 
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              initialType={modalType}
            />

            {/* Trust Indicators */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 pt-8">
              {/* Trustpilot */}
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-[#FF035B]/20">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-4 h-4 fill-[#FF035B]" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <div className="text-left">
                  <div className="text-sm font-bold text-white">Excellent <CounterAnimation end={4.9} decimals={1} />/5</div>
                  <div className="text-xs text-gray-400"><CounterAnimation end={18543} /> satisfied clients</div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Bottom Gradient Wave */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-[#FF035B] via-[#2F1A8E] to-[#FF035B] animate-pulse"></div>
      </div>

      {/* Services Bar */}
      <div className="bg-gradient-to-r from-black via-[#2F1A8E]/20 to-black text-white py-6 border-t border-[#FF035B]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {services.map((service, index) => (
              <div key={index} className="flex items-center gap-3 group cursor-pointer">
                <div className="flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                  <service.icon className="w-6 h-6 text-[#FF035B]" />
                </div>
                <span className="text-sm font-medium group-hover:text-[#FF035B] transition-colors">{service.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;